import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Layout
import Layout from "./components/layout/Layout";
import BackButton from "./components/layout/BackButton";
import PageTracker from "./hooks/PageTracker";

// Public Pages
import HomePage from "./components/pages/HomePage";
import LoginPage from "./components/pages/LoginPage";
import RegisterPage from "./components/pages/RegisterPage";
import RegisterElevePage from "./components/pages/RegisterElevePage";
import ForgotPasswordPage from "./components/pages/ForgotPasswordPage";
import ResetPasswordPage from "./components/pages/ResetPasswordPage";
import AcceptInvitationPage from "./components/pages/AcceptInvitationPage";
import InscriptionFormPage from "./components/pages/InscriptionFormPage";
import ChoixInscriptionPage from "./components/pages/ChoixInscriptionPage";
import InscriptionAdultePage from "./components/pages/InscriptionAdultePage";
import DemandeInfoPage from "./components/pages/DemandeInfoPage";
import TarifsPage from "./components/pages/TarifsPage";
import EspaceApprenantPage from "./components/pages/EspaceApprenantPage";
import EspaceElevePage from "./components/pages/EspaceElevePage";
import { ProgrammesEnfantsPage, ProgrammesAdolescentPage, ProgrammesAdultesPage, ProgrammeKiddoesPage } from "./components/pages/ProgrammePages";
import DevisPage from "./components/pages/DevisPage";
import BlogPage from "./components/pages/BlogPage";
import BlogArticlePage from "./components/pages/BlogArticlePage";
import CambridgePage from "./components/pages/CambridgePage";
import CGVPage from "./components/pages/CGVPage";

// Admin Pages
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminInscriptions from "./components/admin/AdminInscriptions";
import AdminSettings from "./components/admin/AdminSettings";
import AdminCommunications from "./components/admin/AdminCommunications";
import AdminMessages from "./components/admin/AdminMessages";
import AdminCMS from "./components/admin/AdminCMS";
import AdminGroupes from "./components/admin/AdminGroupes";
import AdminEleves from "./components/admin/AdminEleves";
import AdminEleveRequests from "./components/admin/AdminEleveRequests";
import AdminTarifs from "./components/admin/AdminTarifs";
import AdminMessaging from "./components/admin/AdminMessaging";
import AdminDevis from "./components/admin/AdminDevis";
import CalendrierPage from "./components/pages/CalendrierPage";
import EspaceAgence from "./components/admin/EspaceAgence";
import AdminBlog from "./components/admin/AdminBlog";
import AdminInscriptionDetail from "./components/admin/AdminInscriptionDetail";

// ScrollToTop Component - Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Force scroll to top with multiple methods for reliability
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false, requireEleve = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#285BBB]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireEleve && (user.role !== 'eleve' || !user.is_approved)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Contact Page - importé depuis composant séparé
import ContactPage from './components/pages/ContactPage';

// Inscription Confirmation
const InscriptionConfirmation = () => (
  <Layout>
    <div className="min-h-screen bg-[#F5F7FF] flex items-center justify-center py-20">
      <div className="bg-white rounded-xl shadow-xl p-8 max-w-md text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[#2C3C65] mb-4">Inscription envoyée !</h1>
        <p className="text-[#6777A1] mb-6">
          Votre demande d'inscription a été enregistrée. Vous recevrez une confirmation par email.
        </p>
        <a
          href="/"
          className="inline-block px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#2C3C65]"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  </Layout>
);

// Inscription Form with Layout
const InscriptionWithLayout = () => (
  <Layout>
    <InscriptionFormPage />
  </Layout>
);

// Page Mentions Légales
const MentionsLegalesPage = () => (
  <Layout>
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-[#2C3C65] mb-8">Mentions légales</h1>
        <div className="prose prose-lg max-w-none text-[#6777A1]">
          <p className="mb-6">
            Conformément aux dispositions des Articles 6-III et 19 de la Loi n°2004-575 du 21 juin 2004 
            pour la Confiance dans l'économie numérique, dite L.C.E.N., il est porté à la connaissance 
            des Utilisateurs du site les présentes mentions légales.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 1 : L'éditeur</h2>
          <p className="mb-6">
            L'édition du site est assurée par The English Speaking Center.<br />
            Adresse : 9 Allée Jean Jaurès, 13800 Istres<br />
            Email : contact@theenglishspeakingcenter.fr
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 2 : L'hébergeur</h2>
          <p className="mb-6">
            L'hébergeur du site est la Société Planethoster.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 3 : Accès au site</h2>
          <p className="mb-6">
            Le site est accessible par tout endroit, 7j/7, 24h/24 sauf cas de force majeure, 
            interruption programmée ou non et pouvant découler d'une nécessité de maintenance.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 4 : Collecte des données</h2>
          <p className="mb-6">
            Le site collecte des données personnelles dans le cadre des inscriptions aux formations. 
            Ces données sont traitées conformément au RGPD et ne sont jamais revendues à des tiers.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 5 : Cookies</h2>
          <p className="mb-6">
            L'Utilisateur est informé que lors de ses visites sur le site, un cookie peut s'installer 
            automatiquement sur son logiciel de navigation. Un cookie est un élément qui ne permet pas 
            d'identifier l'Utilisateur mais sert à enregistrer des informations relatives à la navigation.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">ARTICLE 6 : Propriété intellectuelle</h2>
          <p className="mb-6">
            Toute utilisation, reproduction, diffusion, commercialisation, modification de toute ou partie 
            du site, sans autorisation de l'Editeur est prohibée et pourra entraîner des actions et 
            poursuites judiciaires.
          </p>
        </div>
      </div>
    </div>
  </Layout>
);

// Page Politique de Confidentialité
const PolitiqueConfidentialitePage = () => (
  <Layout>
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-[#2C3C65] mb-8">Politique de confidentialité</h1>
        <div className="prose prose-lg max-w-none text-[#6777A1]">
          <p className="mb-6">
            The English Speaking Center s'engage à protéger la vie privée des utilisateurs de son site 
            et de ses services. Cette politique de confidentialité explique comment nous collectons, 
            utilisons et protégeons vos données personnelles.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Données collectées</h2>
          <p className="mb-6">
            Nous collectons les données suivantes lors de l'inscription :<br />
            - Nom et prénom<br />
            - Adresse email<br />
            - Numéro de téléphone<br />
            - Adresse postale<br />
            - Informations relatives aux enfants inscrits (nom, prénom, date de naissance)
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Utilisation des données</h2>
          <p className="mb-6">
            Vos données sont utilisées exclusivement pour :<br />
            - La gestion de votre inscription<br />
            - La communication relative aux cours et formations<br />
            - L'envoi d'informations sur nos services<br />
            - Le respect de nos obligations légales
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Conservation des données</h2>
          <p className="mb-6">
            Vos données sont conservées pendant la durée de votre inscription et jusqu'à 3 ans après 
            la fin de votre dernière formation, conformément aux obligations légales.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Vos droits</h2>
          <p className="mb-6">
            Conformément au RGPD, vous disposez des droits suivants :<br />
            - Droit d'accès à vos données<br />
            - Droit de rectification<br />
            - Droit à l'effacement<br />
            - Droit à la portabilité<br />
            - Droit d'opposition<br /><br />
            Pour exercer ces droits, contactez-nous à : contact@theenglishspeakingcenter.fr
          </p>
        </div>
      </div>
    </div>
  </Layout>
);

// Page Règlement Intérieur
const ReglementPage = () => (
  <Layout>
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-[#2C3C65] mb-8">Règlement intérieur</h1>
        <div className="prose prose-lg max-w-none text-[#6777A1]">
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 1 : Inscription</h2>
          <p className="mb-6">
            L'inscription aux cours de The English Speaking Center est soumise à l'acceptation du 
            présent règlement intérieur. Le règlement s'applique à tous les apprenants inscrits 
            ainsi qu'à leurs représentants légaux pour les mineurs.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 2 : Assiduité</h2>
          <p className="mb-6">
            L'assiduité aux cours est obligatoire. Toute absence doit être signalée au plus tôt par 
            email ou téléphone. Les absences non justifiées ne donnent pas lieu à remboursement.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 3 : Comportement</h2>
          <p className="mb-6">
            Les apprenants s'engagent à avoir un comportement respectueux envers les formateurs et 
            les autres apprenants. Tout comportement perturbateur pourra entraîner l'exclusion 
            temporaire ou définitive sans remboursement.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 4 : Matériel</h2>
          <p className="mb-6">
            Les apprenants doivent se présenter aux cours avec le matériel nécessaire (cahier, stylo, 
            manuel si demandé). Le centre décline toute responsabilité en cas de perte ou vol 
            d'effets personnels.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 5 : Paiement</h2>
          <p className="mb-6">
            Le paiement des frais de formation doit être effectué selon les modalités convenues lors 
            de l'inscription. En cas de non-paiement, le centre se réserve le droit de suspendre 
            l'accès aux cours.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 6 : Annulation</h2>
          <p className="mb-6">
            Toute demande d'annulation doit être effectuée par écrit. Les conditions de remboursement 
            dépendent de la date de la demande par rapport au début de la formation.
          </p>
          
          <h2 className="text-2xl font-bold text-[#2C3C65] mt-8 mb-4">Article 7 : Droit à l'image</h2>
          <p className="mb-6">
            Sauf opposition expresse lors de l'inscription, les apprenants autorisent le centre à 
            utiliser leur image dans le cadre de la communication du centre (site web, réseaux sociaux).
          </p>
        </div>
      </div>
    </div>
  </Layout>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <BackButton />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register-eleve" element={<RegisterElevePage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/inscription" element={<ChoixInscriptionPage />} />
            <Route path="/inscription/enfant" element={<InscriptionWithLayout />} />
            <Route path="/inscription/adulte" element={<InscriptionAdultePage />} />
            <Route path="/inscription/confirmation" element={<InscriptionConfirmation />} />
            <Route path="/demande-info" element={<DemandeInfoPage />} />
            <Route path="/tarifs" element={<Navigate to="/demande-info" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/devis" element={<DevisPage />} />
            <Route path="/cambridge" element={<CambridgePage />} />
            <Route path="/calendrier" element={<CalendrierPage />} />
            <Route path="/programmes/kiddoes" element={<ProgrammeKiddoesPage />} />
            <Route path="/programmes/enfants" element={<ProgrammesEnfantsPage />} />
            <Route path="/programmes/adolescents" element={<ProgrammesAdolescentPage />} />
            <Route path="/programmes/adultes" element={<ProgrammesAdultesPage />} />
            
            {/* Pages légales */}
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/cgv" element={<CGVPage />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="/reglement" element={<ReglementPage />} />
            
            {/* Blog */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />

            {/* Protected Routes - Espace Apprenant (Parents) */}
            <Route
              path="/espace-apprenant"
              element={
                <ProtectedRoute>
                  <EspaceApprenantPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Espace Eleve */}
            <Route
              path="/espace-eleve"
              element={
                <ProtectedRoute requireEleve>
                  <EspaceElevePage />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inscriptions"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminInscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inscriptions/:id"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminInscriptionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/eleve-requests"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEleveRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/groupes"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminGroupes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/eleves"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEleves />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/communications"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCommunications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/messages"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cms"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCMS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/espace-agence"
              element={
                <ProtectedRoute requireAdmin>
                  <EspaceAgence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tarifs"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminTarifs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/messaging"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminMessaging />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/devis"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDevis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agence"
              element={
                <ProtectedRoute requireAdmin>
                  <EspaceAgence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBlog />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;
